import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Image, Modal, Row, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowCircleLeft,
  faExternalLinkAlt,
  faCheck,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useParams, useHistory } from 'react-router-dom';

import { backgroundGrey } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import pointerOnHover from 'lib/css/pointerOnHover';
import {
  PRODUCT_ENRICHMENT_PDF_URLS,
  PRODUCT_ENRICHMENT_PDF_APPROVE,
} from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const ApprovalStatusOption = [
  { label: 'Approved', value: 'true' },
  { label: 'Not Approved', value: 'false' },
];

function PDFQAView() {
  const { resourceId } = useParams();
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_UPB_PAGE_SIZE);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(ApprovalStatusOption[1]);
  const [extractedAttributes, setExtractedAttributes] = useState({});

  const {
    data: { pdfs = [], pdf_count = 0 } = {},
    loading,
    refetch,
  } = useGet(PRODUCT_ENRICHMENT_PDF_URLS, {
    product_enrichment_resource_id: resourceId,
    approval_status: approvalStatus.value,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
  });

  const { postData: approvePDF, loading: approvingPDF } = usePost(
    PRODUCT_ENRICHMENT_PDF_APPROVE,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'PDF approval status updated successfully',
      }).then(() => {
        setShowPDFModal(false);
        setSelectedPDF(null);
      });
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred while updating approval status: ${error?.response?.data?.message}`,
      });
    },
  );

  useEffect(() => {
    if (selectedPDF) {
      // Initialize extracted attributes from approved_attributes if available
      setExtractedAttributes(selectedPDF.approved_attributes || {});
    }
  }, [selectedPDF]);

  if (loading) {
    return <LoadingSpinner short />;
  }

  const handleApprove = approved => {
    approvePDF({
      pdf_url_id: selectedPDF.id,
      approved,
      approved_attributes: extractedAttributes,
    });
  };

  const handleViewPDF = pdfUrlId => {
    // Instead of opening a modal, navigate to the detailed view
    history.push(`/product-enrichment-resources/pdf-details/${resourceId}/${pdfUrlId}`);
  };

  const renderPDFViewer = () => {
    if (!selectedPDF) return null;

    const pdfUrl = selectedPDF.pdf_url;
    // Display S3 URL as iframe if it's a PDF, otherwise as an image link
    const isPDF = pdfUrl.toLowerCase().endsWith('.pdf');

    return (
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          setSelectedPDF(null);
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>PDF Document Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col>
              <h5>Document Details</h5>
              <p>
                <strong>Product: </strong>
                {selectedPDF.manufacturer_product_name || selectedPDF.vendor_product_name || 'N/A'}
              </p>
              <p>
                <strong>SKU: </strong>
                {selectedPDF.manufacturer_sku || selectedPDF.vendor_sku || 'N/A'}
              </p>
              <p>
                <strong>Uploaded By: </strong>
                {selectedPDF.uploaded_by || 'N/A'}
              </p>
              {selectedPDF.pdf_approval_status && (
                <p>
                  <strong>Approved By: </strong>
                  {selectedPDF.approved_by || 'N/A'}
                </p>
              )}
            </Col>
            <Col className="text-right">
              {!selectedPDF.pdf_approval_status && (
                <>
                  <Button
                    variant="success"
                    className="mr-2"
                    onClick={() => handleApprove(true)}
                    disabled={approvingPDF}
                  >
                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleApprove(false)}
                    disabled={approvingPDF}
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-1" />
                    Reject
                  </Button>
                </>
              )}
              <div className="mt-2">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                  Open in New Tab
                </a>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {isPDF ? (
                <div className={css(styles.pdfContainer)}>
                  <iframe
                    src={pdfUrl}
                    title="PDF Viewer"
                    width="100%"
                    height="600px"
                    className={css(styles.pdfFrame)}
                  />
                </div>
              ) : (
                <div className="text-center">
                  <Image src={pdfUrl} alt="Document" fluid />
                </div>
              )}
            </Col>
            <Col md={4}>
              <h5>Extracted Attributes</h5>
              <Form>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={15}
                    value={JSON.stringify(extractedAttributes, null, 2)}
                    onChange={e => {
                      try {
                        setExtractedAttributes(JSON.parse(e.target.value));
                      } catch (error) {
                        // Do nothing if JSON is invalid
                      }
                    }}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPDFModal(false);
              setSelectedPDF(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <Container>
      <div className="mb-3 d-flex justify-content-between">
        <h3 className="font-weight-bold">
          <FontAwesomeIcon
            className={classNames(pointerOnHover, 'mr-2')}
            icon={faArrowCircleLeft}
            onClick={() => history.push('/product-enrichment-resources/pdf-upload')}
          />
          PDF Document Quality Assurance
        </h3>
      </div>

      <Row>
        <Col className="w-100 d-flex justify-content-between px-3 py-2">
          <Form.Group className={css(styles.select)}>
            <Form.Label>Approval Status</Form.Label>
            <Select
              value={approvalStatus}
              options={ApprovalStatusOption}
              onChange={option => setApprovalStatus(option)}
            />
          </Form.Group>
        </Col>
        <Col>
          <div className="float-right px-3 pb-3">
            <p>{pdf_count} PDF Documents</p>
            <PaginationComponent
              totalItems={pdf_count}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              disabled={loading}
            />
          </div>
        </Col>
      </Row>

      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>ID</th>
            <th>Product Name</th>
            <th>SKU</th>
            <th>Product Type</th>
            <th>Uploaded By</th>
            <th>Approval Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {pdfs.length ? (
            pdfs.map((pdf, index) => (
              <tr key={index}>
                <td>{pdf.id}</td>
                <td>{pdf.manufacturer_product_name || pdf.vendor_product_name || 'N/A'}</td>
                <td>{pdf.manufacturer_sku || pdf.vendor_sku || 'N/A'}</td>
                <td>{pdf.product_type}</td>
                <td>{pdf.uploaded_by || 'N/A'}</td>
                <td>
                  {pdf.pdf_approval_status ? (
                    <span className="badge badge-success">Approved</span>
                  ) : (
                    <span className="badge badge-danger">Not Approved</span>
                  )}
                </td>
                <td>
                  <Button variant="outline-primary" size="sm" onClick={() => handleViewPDF(pdf.id)}>
                    View & QA
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="text-center">
                No PDF documents found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {renderPDFViewer()}
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  select: {
    width: '50%',
  },
  pdfContainer: {
    width: '100%',
    height: '600px',
    border: '1px solid #ccc',
    overflow: 'auto',
  },
  pdfFrame: {
    border: 'none',
  },
});

PDFQAView.propTypes = {
  resourceId: PropTypes.number,
};

export default PDFQAView;
